// src/components/admin/ProgramManagement.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { styles } from '../../styles/theme';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

const to12Hour = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')}${ampm}`;
};

const ALL_DAYS = [
  { id: 'monday',    label: 'Mon' },
  { id: 'tuesday',   label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday',  label: 'Thu' },
  { id: 'friday',    label: 'Fri' },
  { id: 'saturday',  label: 'Sat' },
];

const EMPTY_FORM = {
  name: '',
  locationId: '',
  locationName: '',
  center: 'redmond',
  defaultStaffNeeded: 1,
  activeDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  defaultStartTime: '',
  defaultEndTime: '',
  startDate: '',
  sortOrder: 99,
  active: true,
  staffingType: 'fixed',
  legacyProgramTypes: '',
};

const ProgramManagement = ({ onBack }) => {
  const [programs, setPrograms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Holidays state
  const [holidays, setHolidays] = useState([]);
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [holidayForm, setHolidayForm] = useState({
    name: '', startDate: '', endDate: '',
    type: 'program', entityId: '', entityName: '', notes: ''
  });
  const [savingHoliday, setSavingHoliday] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [progSnap, locSnap, holidaySnap] = await Promise.all([
        getDocs(collection(db, 'programs')),
        getDocs(collection(db, 'locations')),
        getDocs(collection(db, 'holidays')),
      ]);
      const progList = progSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99));
      const locList = locSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(l => l.active)
        .sort((a, b) => a.name.localeCompare(b.name));
      setPrograms(progList);
      setLocations(locList);

      // Load holidays
      const holidayList = holidaySnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.startDate.localeCompare(b.startDate));
      setHolidays(holidayList);
    } catch (err) {
      console.error('Error loading data:', err);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingProgram(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      name: program.name || '',
      locationId: program.locationId || '',
      locationName: program.locationName || '',
      center: program.center || 'redmond',
      defaultStaffNeeded: program.defaultStaffNeeded || 1,
      activeDays: program.activeDays || ['monday','tuesday','wednesday','thursday','friday'],
      defaultStartTime: program.defaultStartTime || '',
      defaultEndTime: program.defaultEndTime || '',
      startDate: program.startDate || '',
      sortOrder: program.sortOrder ?? 99,
      active: program.active !== false,
      staffingType: program.staffingType || 'fixed',
      legacyProgramTypes: (program.legacyProgramTypes || []).join(', '),
    });
    setShowModal(true);
  };

  const handleLocationChange = (locId) => {
    const loc = locations.find(l => l.id === locId);
    setFormData(f => ({ ...f, locationId: locId, locationName: loc?.name || '' }));
  };

  const toggleDay = (dayId) => {
    setFormData(f => ({
      ...f,
      activeDays: f.activeDays.includes(dayId)
        ? f.activeDays.filter(d => d !== dayId)
        : [...f.activeDays, dayId],
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.locationId) {
      alert('Program name and location are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        legacyProgramTypes: formData.legacyProgramTypes
          ? formData.legacyProgramTypes.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        updatedAt: serverTimestamp(),
      };
      if (editingProgram) {
        await updateDoc(doc(db, 'programs', editingProgram.id), payload);
      } else {
        await addDoc(collection(db, 'programs'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      setShowModal(false);
      await loadAll();
    } catch (err) {
      console.error('Error saving program:', err);
      alert('Failed to save program.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (program) => {
    if (!window.confirm(`Archive "${program.name}"? It will be hidden from the scheduler but all historical schedule records will be preserved. You can restore it by editing the program.`)) return;
    try {
      await updateDoc(doc(db, 'programs', program.id), {
        active: false,
        archived: true,
        archivedAt: serverTimestamp(),
      });
      await loadAll();
    } catch (err) {
      console.error('Error archiving program:', err);
      alert('Failed to archive program.');
    }
  };

  const handleToggleActive = async (program) => {
    try {
      await updateDoc(doc(db, 'programs', program.id), { active: !program.active });
      await loadAll();
    } catch (err) {
      console.error('Error toggling program:', err);
    }
  };

  const handleSaveHoliday = async () => {
    if (!holidayForm.name || !holidayForm.startDate || !holidayForm.endDate) {
      alert('Please fill in name, start date and end date.');
      return;
    }
    setSavingHoliday(true);
    try {
      await addDoc(collection(db, 'holidays'), {
        ...holidayForm,
        createdAt: new Date().toISOString(),
      });
      setHolidayForm({ name: '', startDate: '', endDate: '', type: 'program', entityId: '', entityName: '', notes: '' });
      setShowHolidayForm(false);
      await loadAll();
    } catch (err) {
      alert('Failed to save holiday.');
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await deleteDoc(doc(db, 'holidays', id));
      await loadAll();
    } catch (err) {
      alert('Failed to delete holiday.');
    }
  };

  const formatDateDisplay = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${m}/${day}/${y}`;
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onBack} style={styles.secondaryButton}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>🎯 Program Management</h1>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
              Manage programs shown in the staff scheduler
            </p>
          </div>
          <button onClick={openAdd} style={{ ...styles.button, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Plus size={16} /> Add Program
          </button>
        </div>
      </div>

      {/* Programs List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Loading programs...</div>
      ) : programs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          No programs yet. Add your first program above.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {programs.map(program => (
            <div key={program.id} style={{
              ...styles.card,
              padding: '1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '1rem',
              opacity: program.active ? 1 : 0.55,
              borderLeft: `4px solid ${program.active ? '#3b82f6' : '#d1d5db'}`,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1f2937' }}>
                    {program.name}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>@</span>
                  <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#3b82f6' }}>
                    {program.locationName}
                  </span>
                  <span style={{
                    fontSize: '0.68rem', fontWeight: '700', padding: '0.1rem 0.45rem', borderRadius: '4px',
                    backgroundColor: program.center === 'kirkland' ? '#d1fae5' : program.center === 'both' ? '#dbeafe' : '#fee2e2',
                    color: program.center === 'kirkland' ? '#065f46' : program.center === 'both' ? '#1e40af' : '#991b1b',
                  }}>
                    {program.center === 'kirkland' ? '📍 Kirkland' : program.center === 'both' ? '📍 Both' : '📍 Redmond'}
                  </span>
                  {!program.active && (
                    <span style={{ fontSize: '0.7rem', backgroundColor: '#f3f4f6', color: '#6b7280', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '600' }}>
                      Inactive
                    </span>
                  )}
                  {program.startDate && (
                    <span style={{ fontSize: '0.7rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '600' }}>
                      Starts {program.startDate}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <span>👥 {program.defaultStaffNeeded} staff {program.staffingType === 'flexible' ? 'min (flexible)' : 'needed'}</span>
                  {program.defaultStartTime && program.defaultEndTime && (
                    <span>🕐 {to12Hour(program.defaultStartTime)} – {to12Hour(program.defaultEndTime)}</span>
                  )}
                  <span>
                    📅 {ALL_DAYS.filter(d => program.activeDays?.includes(d.id)).map(d => d.label).join(', ')}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button
                  onClick={() => handleToggleActive(program)}
                  title={program.active ? 'Deactivate' : 'Activate'}
                  style={{
                    padding: '0.4rem 0.75rem',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    backgroundColor: program.active ? '#fef3c7' : '#d1fae5',
                    color: program.active ? '#92400e' : '#065f46',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  {program.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => openEdit(program)}
                  style={{ padding: '0.4rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => handleDelete(program)}
                  title="Archive program (preserves history)"
                  style={{ padding: '0.4rem 0.6rem', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                >
                  <Trash2 size={14} /> Archive
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700' }}>
                {editingProgram ? 'Edit Program' : 'Add New Program'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <X size={20} />
              </button>
            </div>

            {/* Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Program Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                style={styles.input}
                placeholder="e.g. After-School, Full-Day Camp"
              />
            </div>

            {/* Location */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Location *</label>
              <select
                value={formData.locationId}
                onChange={e => handleLocationChange(e.target.value)}
                style={styles.input}
              >
                <option value="">Select location...</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>

            {/* Center */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Center *</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[
                  { value: 'redmond', label: '📍 Redmond', bg: '#fee2e2', color: '#991b1b', active: '#fca5a5' },
                  { value: 'kirkland', label: '📍 Kirkland', bg: '#d1fae5', color: '#065f46', active: '#6ee7b7' },
                  { value: 'both', label: '📍 Both', bg: '#dbeafe', color: '#1e40af', active: '#93c5fd' },
                ].map(opt => (
                  <button key={opt.value} onClick={() => setFormData(f => ({ ...f, center: opt.value }))}
                    style={{
                      flex: 1, padding: '0.55rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem',
                      border: `2px solid ${formData.center === opt.value ? opt.active : '#e5e7eb'}`,
                      backgroundColor: formData.center === opt.value ? opt.bg : 'white',
                      color: formData.center === opt.value ? opt.color : '#6b7280',
                      fontWeight: formData.center === opt.value ? '700' : '400',
                    }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Staff Needed + Sort Order */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={styles.label}>Staff Needed</label>
                <input
                  type="number"
                  min="1"
                  value={formData.defaultStaffNeeded}
                  onChange={e => setFormData(f => ({ ...f, defaultStaffNeeded: parseInt(e.target.value) || 1 }))}
                  style={styles.input}
                />
              </div>
              <div>
                <label style={styles.label}>Sort Order</label>
                <input
                  type="number"
                  min="1"
                  value={formData.sortOrder}
                  onChange={e => setFormData(f => ({ ...f, sortOrder: parseInt(e.target.value) || 99 }))}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Default Times */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={styles.label}>Default Start Time</label>
                <input
                  type="time"
                  value={formData.defaultStartTime}
                  onChange={e => setFormData(f => ({ ...f, defaultStartTime: e.target.value }))}
                  style={styles.input}
                />
              </div>
              <div>
                <label style={styles.label}>Default End Time</label>
                <input
                  type="time"
                  value={formData.defaultEndTime}
                  onChange={e => setFormData(f => ({ ...f, defaultEndTime: e.target.value }))}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Start Date */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Program Start Date (optional — leave blank if ongoing)</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={e => setFormData(f => ({ ...f, startDate: e.target.value }))}
                style={styles.input}
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                Set this for new programs that haven't started yet (e.g. Cascade Elementary Apr 16)
              </p>
            </div>

            {/* Active Days */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={styles.label}>Active Days</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                {ALL_DAYS.map(day => {
                  const active = formData.activeDays.includes(day.id);
                  return (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => toggleDay(day.id)}
                      style={{
                        padding: '0.4rem 0.75rem',
                        border: '2px solid',
                        borderColor: active ? '#3b82f6' : '#e5e7eb',
                        borderRadius: '6px',
                        backgroundColor: active ? '#eff6ff' : 'white',
                        color: active ? '#1e40af' : '#6b7280',
                        fontSize: '0.85rem',
                        fontWeight: active ? '700' : '400',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Staffing Type */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Staffing Type</label>
              <select
                value={formData.staffingType}
                onChange={e => setFormData(f => ({ ...f, staffingType: e.target.value }))}
                style={styles.input}
              >
                <option value="fixed">Fixed — strict staff count required</option>
                <option value="flexible">Flexible — multiple shifts, no cap (e.g. Full-Day Camp)</option>
              </select>
              {formData.staffingType === 'flexible' && (
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                  Flexible programs show a soft minimum but allow unlimited shift assignments per day.
                </p>
              )}
            </div>

            {/* Legacy Program Types */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={styles.label}>Legacy Program Names (optional)</label>
              <input
                type="text"
                value={formData.legacyProgramTypes}
                onChange={e => setFormData(f => ({ ...f, legacyProgramTypes: e.target.value }))}
                placeholder="e.g. After-School, Full-Day Camp"
                style={styles.input}
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                Comma-separated list of old program names this replaces — used to match historical schedule records.
              </p>
            </div>

            {/* Active toggle */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={e => setFormData(f => ({ ...f, active: e.target.checked }))}
                />
                <span style={{ fontSize: '0.875rem' }}>Active (show in scheduler)</span>
              </label>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{ ...styles.button, flex: 1, backgroundColor: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
              >
                <X size={16} /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ ...styles.button, flex: 1, backgroundColor: saving ? '#9ca3af' : '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
              >
                <Save size={16} /> {saving ? 'Saving...' : editingProgram ? 'Update' : 'Add Program'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Holidays Section ── */}
      <div style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>🗓️ School & Program Holidays</h2>
            <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.2rem 0 0' }}>
              Date ranges when a school or program is closed — shown as indicators in the scheduler
            </p>
          </div>
          <button onClick={() => setShowHolidayForm(v => !v)} style={{ ...styles.button, display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: showHolidayForm ? '#6b7280' : '#3b82f6' }}>
            <Plus size={16} /> {showHolidayForm ? 'Cancel' : 'Add Holiday'}
          </button>
        </div>

        {/* Add Holiday Form */}
        {showHolidayForm && (
          <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={styles.label}>Holiday / Break Name *</label>
                <input type="text" value={holidayForm.name} onChange={e => setHolidayForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Spring Break" style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Type *</label>
                <select value={holidayForm.type} onChange={e => setHolidayForm(f => ({ ...f, type: e.target.value, entityId: '', entityName: '' }))} style={styles.input}>
                  <option value="program">Specific Program</option>
                  <option value="school">Specific School</option>
                  <option value="all">All Programs</option>
                </select>
              </div>
            </div>

            {/* Entity selector */}
            {holidayForm.type === 'program' && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={styles.label}>Program *</label>
                <select value={holidayForm.entityId} onChange={e => {
                  const p = programs.find(p => p.id === e.target.value);
                  setHolidayForm(f => ({ ...f, entityId: e.target.value, entityName: p ? `${p.name} – ${p.locationName}` : '' }));
                }} style={styles.input}>
                  <option value="">Select program...</option>
                  {programs.filter(p => p.active).map(p => (
                    <option key={p.id} value={p.id}>{p.name} – {p.locationName}</option>
                  ))}
                </select>
              </div>
            )}
            {holidayForm.type === 'school' && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={styles.label}>Location / School *</label>
                <select value={holidayForm.entityName} onChange={e => setHolidayForm(f => ({ ...f, entityId: e.target.value, entityName: e.target.value }))} style={styles.input}>
                  <option value="">Select location...</option>
                  {locations.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
                </select>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={styles.label}>Start Date *</label>
                <input type="date" value={holidayForm.startDate} onChange={e => setHolidayForm(f => ({ ...f, startDate: e.target.value }))} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>End Date *</label>
                <input type="date" value={holidayForm.endDate} onChange={e => setHolidayForm(f => ({ ...f, endDate: e.target.value }))} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Notes (optional)</label>
                <input type="text" value={holidayForm.notes} onChange={e => setHolidayForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. No after school" style={styles.input} />
              </div>
            </div>

            <button onClick={handleSaveHoliday} disabled={savingHoliday} style={{ ...styles.button, backgroundColor: savingHoliday ? '#9ca3af' : '#10b981' }}>
              {savingHoliday ? 'Saving...' : 'Save Holiday'}
            </button>
          </div>
        )}

        {/* Holidays List */}
        {holidays.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb', fontSize: '0.875rem' }}>
            No holidays added yet. Add school calendars and break dates above.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {holidays.map(h => (
              <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1f2937' }}>{h.name}</span>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {formatDateDisplay(h.startDate)} – {formatDateDisplay(h.endDate)}
                  </span>
                  <span style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: '600',
                    backgroundColor: h.type === 'all' ? '#ede9fe' : h.type === 'school' ? '#dbeafe' : '#d1fae5',
                    color: h.type === 'all' ? '#6d28d9' : h.type === 'school' ? '#1e40af' : '#065f46'
                  }}>
                    {h.type === 'all' ? 'All Programs' : h.type === 'school' ? `School: ${h.entityName}` : h.entityName}
                  </span>
                  {h.notes && <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{h.notes}</span>}
                </div>
                <button onClick={() => handleDeleteHoliday(h.id)} style={{ padding: '0.3rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramManagement;